<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactLabels from 'dashboard/components-next/Contacts/ContactLabels/ContactLabels.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import { formatWhatsappUsername } from 'dashboard/helper/whatsappUsername';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
});

const { t, locale } = useI18n();
const store = useStore();
const isAssignedOnly = useMapGetter('isAssignedOnly');

const avatarFile = ref(null);
const avatarUrl = ref('');

const contactsFormRef = ref(null);

const uiFlags = useMapGetter('contacts/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);

const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);
const whatsappUsername = computed(() =>
  formatWhatsappUsername(props.selectedContact?.additionalAttributes)
);
const whatsappUsernameHistory = computed(
  () => props.selectedContact?.whatsappUsernameHistory || []
);
const copyWhatsappUsername = async () => {
  await copyTextToClipboard(whatsappUsername.value);
  useAlert(t('CONTACT_PANEL.COPY_SUCCESSFUL'));
};
const formatSeenAt = value => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : new Intl.DateTimeFormat(locale.value, {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: 'America/Bogota',
      }).format(date);
};

const contactData = ref({});

const getInitialContactData = () => {
  if (!props.selectedContact) return {};
  return { ...props.selectedContact };
};

onMounted(() => {
  Object.assign(contactData.value, getInitialContactData());
});

const createdAt = computed(() => {
  return contactData.value?.createdAt
    ? dynamicTime(contactData.value.createdAt)
    : '';
});

const lastActivityAt = computed(() => {
  return contactData.value?.lastActivityAt
    ? dynamicTime(contactData.value.lastActivityAt)
    : '';
});

const avatarSrc = computed(() => {
  return avatarUrl.value ? avatarUrl.value : contactData.value?.thumbnail;
});

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const updateContact = async () => {
  try {
    const { customAttributes, ...basicContactData } = contactData.value;
    await store.dispatch('contacts/update', basicContactData);
    await store.dispatch(
      'contacts/fetchContactableInbox',
      props.selectedContact.id
    );
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.ERROR_MESSAGE'));
  }
};

const handleAvatarUpload = async ({ file, url }) => {
  avatarFile.value = file;
  avatarUrl.value = url;

  try {
    await store.dispatch('contacts/update', {
      ...contactsFormRef.value?.state,
      avatar: file,
      isFormData: true,
    });
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.ERROR_MESSAGE'));
  }
};

const handleAvatarDelete = async () => {
  try {
    if (props.selectedContact && props.selectedContact.id) {
      await store.dispatch('contacts/deleteAvatar', props.selectedContact.id);
      useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.SUCCESS_MESSAGE'));
    }
    avatarFile.value = null;
    avatarUrl.value = '';
    contactData.value.thumbnail = null;
  } catch (error) {
    useAlert(
      error.message
        ? error.message
        : t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <div class="flex flex-col items-start gap-8 pb-6">
    <div class="flex flex-col items-start gap-3">
      <Avatar
        :src="avatarSrc || ''"
        :name="selectedContact?.name || ''"
        :size="72"
        :allow-upload="!isAssignedOnly"
        @upload="handleAvatarUpload"
        @delete="handleAvatarDelete"
      />
      <div class="flex flex-col gap-1">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ selectedContact?.name }}
        </h3>
        <div class="flex flex-col gap-1.5">
          <span
            v-if="selectedContact?.identifier"
            class="inline-flex items-center gap-1 text-sm text-n-slate-11"
          >
            <span class="i-ph-user-gear text-n-slate-10 size-4" />
            {{ selectedContact?.identifier }}
          </span>
          <div
            v-if="whatsappUsername"
            class="inline-flex items-center gap-1 text-sm text-n-slate-11"
            :title="t('CONTACT_PANEL.WHATSAPP_USERNAME')"
          >
            <span
              class="i-ri-whatsapp-line text-n-slate-10 size-4"
              aria-hidden="true"
            />
            {{ whatsappUsername }}
            <button
              type="button"
              :aria-label="t('WHATSAPP_IDENTITY.COPY')"
              class="ml-1 rounded p-1 hover:bg-n-slate-3 focus-visible:outline focus-visible:outline-n-brand"
              @click="copyWhatsappUsername"
            >
              <span class="i-lucide-clipboard size-3.5" aria-hidden="true" />
            </button>
          </div>
          <span class="inline-flex items-center gap-1 text-sm text-n-slate-11">
            <span
              v-if="selectedContact?.identifier"
              class="i-ph-activity text-n-slate-10 size-4"
            />
            {{ $t('CONTACTS_LAYOUT.DETAILS.CREATED_AT', { date: createdAt }) }}
            •
            {{
              $t('CONTACTS_LAYOUT.DETAILS.LAST_ACTIVITY', {
                date: lastActivityAt,
              })
            }}
          </span>
        </div>
      </div>
      <ContactLabels :contact-id="selectedContact?.id" />
      <details
        v-if="whatsappUsernameHistory.length"
        class="w-full rounded-lg border border-n-weak bg-n-surface-2 p-3 text-sm text-n-slate-11"
      >
        <summary class="cursor-pointer font-medium text-n-slate-12">
          {{ t('WHATSAPP_IDENTITY.HISTORY') }}
        </summary>
        <ul class="mb-0 mt-3 flex flex-col gap-2 pl-4">
          <li
            v-for="entry in whatsappUsernameHistory"
            :key="`${entry.inboxId}-${entry.username}`"
          >
            <span class="font-medium text-n-slate-12">
              {{
                formatWhatsappUsername({
                  social_whatsapp_user_name: entry.username,
                })
              }}
            </span>
            <span v-if="entry.lastSeenAt" class="ml-2 text-xs">
              {{ formatSeenAt(entry.lastSeenAt) }}
            </span>
          </li>
        </ul>
      </details>
    </div>
    <div class="flex flex-col items-start gap-6">
      <fieldset :disabled="isAssignedOnly" class="m-0 p-0 border-0">
        <ContactsForm
          ref="contactsFormRef"
          :contact-data="contactData"
          is-details-view
          @update="handleFormUpdate"
        />
      </fieldset>
      <Button
        v-if="!isAssignedOnly"
        :label="t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')"
        size="sm"
        :is-loading="isUpdating"
        :disabled="isUpdating || isFormInvalid"
        @click="updateContact"
      />
    </div>
  </div>
</template>
